import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router';
import LoaderSpinner from "../../common/Loader";
import BatchPagination from "../../batch/BatchPagination";
import { CarrierConnectAssignmentApprovalColumns, numOfPage } from "../../../constant";
import { toast } from "react-toastify";
import ApprovalModal from "./ApprovalModal";
import BulkApprovalModal from "./BulkApprovalModal";
import "./CarrierConnectApproval.css";
import { getCarrierConnectListing, updatePackageCount } from "../../../store/actions/carrierConnect.action";
import { carrierConnectBackDetails } from "../../../store/actions/employee";
import { approveCarrierConnectService } from "../../../services/carrierConnect.service";
import { getStatusClass } from '../../../config/utils';
import DataTable from "../../common/DataTable";
import _ from "lodash";
import ViewImage from "../../common/ViewImage";

const CarrierConnectApproval = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userDetails } = useSelector((state) => state.auth);
    const {carrierConnectStatus } = useSelector((state) => state.carrierConnect);
    const loading = carrierConnectStatus === "PENDING";

    // State management
    const [data, setData] = useState([]);
    const [showLoader, setShowLoader] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [imagePreviewRecord, setImagePreviewRecord] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    
    // Modal states
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showBulkApprovalModal, setShowBulkApprovalModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [bulkAction, setBulkAction] = useState("");

    // Filters - using same structure as main listing
    const [filterData, setFilterData] = useState({
        pageIndex: 1,
        pageSize: 30,
        sortColumn: "CreatedDate",
        sortOrder: "DESC",
        searchKeyword: "",
        statusId: null,
        fromDate: null,
        toDate: null,
        isApprovalScreen: true
    });

    // Check if user is super admin
    const isSuperAdmin = userDetails?.Value?.isSuperAdmin || true; // Temporarily allowing all for testing

    const fetchCarrierConnectAssignments = async () => {
        try {
            // Build API data structure similar to main listing
            setShowLoader(true);
            const apiData = {
                pageIndex: filterData.pageIndex,
                pageSize: filterData.pageSize,
                sortColumn: filterData.sortColumn,
                sortOrder: filterData.sortOrder.toUpperCase(),
                searchKeyword: filterData.searchKeyword || "",
                statusId: filterData.statusId,
                fromDate: filterData.fromDate,
                toDate: filterData.toDate,
                carrierId: null,
                isApprovalScreen: filterData.isApprovalScreen
            };
            const result = await Promise.resolve(dispatch(getCarrierConnectListing(apiData)));
            // Handle different response structures
            let listData = [];
            let totalRecords = 0;
            if (result?.payload) {
                // If result has payload structure
                let updateStatusList = result.payload.EntityList.map((item, index) => {
                    return {
                        ...item,
                        listIndex: index,
                        statusName: `<span class='statusTd'><span class="${getStatusClass(item.statusName)}">${item.statusName}</span></span>`
                    };
                });
                listData = updateStatusList || result.payload.EntityList || [];
                totalRecords = result.payload.TotalRecords || result.payload.totalRecords || 0;
            } else if (result?.data) {
                // If result has data structure
                listData = result.data.EntityList || result.data || [];
                totalRecords = result.data.TotalRecords || result.data.totalRecords || 0;
            } else if (Array.isArray(result)) {
                // If result is directly an array
                listData = result;
                totalRecords = result.length;
            } else {
                console.warn("Unexpected API response structure:", result);
            }
            setData(Array.isArray(listData) ? listData : []);
            setTotalRecords(totalRecords);
            setShowLoader(false);
        } catch (error) {
            toast.error("Failed to fetch carrier connect assignments");
            setData([]);
            setTotalRecords(0);
        }
    };

    useEffect(() => {
        fetchCarrierConnectAssignments();
    }, [filterData.pageIndex, filterData.pageSize, filterData.sortColumn, filterData.sortOrder]);

    // Handle approve individual assignment
    const handleApprove = async (row) => {
        try {
            let data = {
                carrierConnectIds: [row.id]
            }
            const response = await approveCarrierConnectService(data);
            if (response.status === 200 || response.data?.Success) {
                toast.success("Carrier Connect assignment approved successfully!");
                // Refresh the data to reflect the approval
                await fetchCarrierConnectAssignments();
            } else {
                throw new Error(response.data?.Message || "Failed to approve assignment");
            }
        } catch (error) {
            toast.error(
                error.response?.data?.Message || 
                error.message || 
                "Failed to approve assignment. Please try again."
            );
        }
    };

    // Handle edit individual assignment
    const handleEdit = (row) => {
        // Save current state for navigation back to approval screen
        const stateToSave = {
            ...filterData,
            searchKeyword: filterData.searchKeyword || '',
            isApprovalScreen: true // Flag to identify this is from approval screen
        };
        
        dispatch(carrierConnectBackDetails(stateToSave));
        
        // Navigate to carrier connect edit page
        navigate(`/carrier-connect/${row.id}`, {
            state: {
                data: row,
                isEdit: true,
                searchState: stateToSave,
                fromApprovalScreen: true // Flag to help return to approval screen
            }
        });
    };

    // Handle view individual assignment
    const handleView = (row) => {
        // Save current state for navigation back to approval screen
        const stateToSave = {
            ...filterData,
            searchKeyword: filterData.searchKeyword || '',
            isApprovalScreen: true // Flag to identify this is from approval screen
        };
        
        dispatch(carrierConnectBackDetails(stateToSave));
        
        // Navigate to carrier connect view page (same route as edit but with isEdit: false)
        navigate(`/carrier-connect/${row.id}`, {
            state: {
                data: row,
                isEdit: false, // This will set isEditable to false for view mode
                isView: true,
                searchState: stateToSave,
                fromApprovalScreen: true // Flag to help return to approval screen
            }
        });
    };

    // Handle bulk approve
    const handleBulkApprove = () => {
        if (selectedRows.length === 0) {
            toast.warning("Please select assignments to approve");
            return;
        }
        setBulkAction("approve");
        setShowBulkApprovalModal(true);
    };

    // Handle bulk approval confirmation
    const handleBulkApprovalConfirm = async (comments) => {
        try {
            let data = {
                carrierConnectIds: _.map(selectedRows, 'id')
            }
            let response = await approveCarrierConnectService(data);
            if (response.status !== 200 && !response.data?.Success) {
                throw new Error(response.data?.Message || "Failed to approve assignments");
            } else {
                setBulkAction("");
                setShowBulkApprovalModal(false);
                toast.success(`${selectedRows.length} assignments approved successfully`);
                fetchCarrierConnectAssignments();
                setSelectedRows([]);
            }
            // Refresh data after action
        } catch (error) {
            console.error("Error performing bulk action:", error);
            toast.error("Failed to perform bulk action");
        }
    };

    // Handle individual approval confirmation
    const handleConfirmAction = async (comments) => {
        try {
            toast.success("Assignment approved successfully");
            // Refresh data after action
            await fetchCarrierConnectAssignments();
            setShowApprovalModal(false);
            setSelectedRowData(null);
            setBulkAction("");
            } catch (error) {
            toast.error("Failed to perform action");
        }
    };

    // Don't render if not super admin
    if (!isSuperAdmin) {
        return (
            <div className="main__container">
                <div className="alert alert-danger">
                    Access Denied: This module is only available for Super Administrators.
                </div>
            </div>
        );
    };

    const handleAction = (item, type, actionObj = {}) => {
        if (type === 'inlineUpdate') {
            handlePackageInlineUpdate(item, actionObj);
            return;
        }
        if (type === 'approve') {
            handleApprove(item);
            return;
        }
        if (type === 'edit') {
            handleEdit(item);
            return;
        }
        if (type === 'view') {
            handleView(item);
            return;
        }
        if (type === 'thumbnail') {
            handlePreviewImage(item);
            return;
        }
    };

    const handlePackageInlineUpdate = async (item, actionObj) => {
        if (Number(actionObj.value) === Number(item.packageCount)) {
            return;
        }
        let payload = `id=${item.id}&packageCount=${actionObj.value}`;
        try {
            const response = await updatePackageCount(payload);
            if (response?.data?.Status === 200) {
                toast.success('Package count updated successfully');
                fetchCarrierConnectAssignments();
            }
        } catch (error) {
            toast.error('Failed to update package count');
        }
    };

    const handleSortingColumn = (key) => {
        let newSortOrder = filterData.sortOrder === "ASCENDING" ? "DESCENDING" : "ASCENDING";
        setFilterData({
            ...filterData,
            sortColumn: key,
            sortOrder: newSortOrder,
            pageIndex: 1
        });
    };

    const handleCheckRecord = (e, type) => {
        const { checked } = e.target;
        let recordData = [...data];
        if (_.isEmpty(type)) {
            let markCheck = recordData.map((item) => ({
                ...item,
                isChecked: checked,
            }));
            setData(markCheck);
        } else {
            recordData[type.listIndex].isChecked = checked;
            setData(recordData);
            if (!checked) {
                let filteredData = selectedRows.filter(
                    (item) => item.id !== recordData[type.listIndex].id
                );
                setSelectedRows(filteredData);
                return;
            } else {
                setSelectedRows([...selectedRows, recordData[type.listIndex]]);
            }
        }
    };

    const handlePreviewImage = (item) => {
        setImagePreviewRecord(item);
        setShowImageModal(true);
    };

    return (
        <main className="carrier-connect-approval">
            <div className="main__container">
                {showLoader && (<div className="full-screen-spinner-overlay"><LoaderSpinner /></div>)}
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>Carrier Connect Assignment Approval</span>
                    <div className="top_right_button_container">
                        {selectedRows.length > 0 && (
                            <button
                                className="btn btn-success top-btn m2"
                                onClick={handleBulkApprove}
                                disabled={loading}
                            >
                                Bulk Approve ({selectedRows.length})
                            </button>
                        )}
                    </div>
                </div>

                {loading && <LoaderSpinner />}

                {/* Main Content */}
                <div className="main_content cus-page-content-sec">
                    <div className="main_content_table_history">
                        <div className="tableFixHead">
                        <DataTable
                            headers={CarrierConnectAssignmentApprovalColumns}
                            tableData={data}
                            actionType={11}
                            handleAction={handleAction}
                            tableDataStatus={loading}
                            handleCheckRecord={handleCheckRecord}
                            isSelectAll={data?.filter(item => item.isChecked).length === data.length}
                            handleSortingColumn={handleSortingColumn}
                            filterData={filterData}
                        />
                    </div>
                    </div>

                    {/* Pagination */}
                    <BatchPagination
                        gridData={data || []}
                        totalVisualRecords={data?.length}
                        numberOfPage={filterData.pageSize}
                        numOfPage={numOfPage}
                        loader={loading}
                        currentPage={filterData.pageIndex}
                        totalPages={Math.ceil(totalRecords / filterData.pageSize) || 0}
                        totalRecords={totalRecords}
                        setCurrentPage={(value) => {
                            setFilterData({ ...filterData, pageIndex: value });
                        }}
                        setNumberOfPage={(value) => {
                            setFilterData({ ...filterData, pageSize: value, pageIndex: 1 });
                        }}
                    />
                </div>

                {showImageModal && (
                    <ViewImage
                        selectedRecord={imagePreviewRecord}
                        showHideImageModal={showImageModal}
                        setShowHideImageModal={setShowImageModal}
                        title="Preview"
                    />
                )}

                {/* Approval Modal */}
                <ApprovalModal
                    show={showApprovalModal}
                    onHide={() => setShowApprovalModal(false)}
                    onConfirm={handleConfirmAction}
                    action={bulkAction}
                    item={selectedRowData}
                />

                {/* Bulk Approval Modal */}
                <BulkApprovalModal
                    show={showBulkApprovalModal}
                    onHide={() => setShowBulkApprovalModal(false)}
                    onConfirm={handleBulkApprovalConfirm}
                    action={bulkAction}
                    selectedItems={selectedRows}
                />
            </div>
        </main>
    );
};

export default CarrierConnectApproval;